/*
  Verano — scroll reveal
  ----------------------
  Adds a gentle fade + rise to content sections as they enter the viewport.

  SAFETY FIRST
  ------------
  A reveal effect must never be able to leave content permanently invisible.
  An earlier version relied solely on IntersectionObserver and hid the whole page
  below the hero when IO callbacks were not delivered. This version therefore has
  three independent triggers, any one of which is sufficient:

    1. IntersectionObserver  (primary, cheapest)
    2. A throttled scroll/resize check on both window and document capture phase,
       which also covers themes that scroll an inner container rather than the
       document — this theme sets `body { overflow: hidden }` and does exactly that.
    3. A hard safety timeout that reveals everything still hidden, no matter what.

  Other deliberate choices:
  - Bails entirely on prefers-reduced-motion.
  - The hiding class is applied from JS, so if this file fails to parse or load,
    the page renders normally.
  - Skips the first content section (the hero) to protect Largest Contentful Paint.
  - Measures after `load`, not just DOMContentLoaded, so image-driven layout shifts
    don't cause a section to be mis-measured as off-screen.
*/
(function veranoReveal() {
  const REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (REDUCED) return;

  const CHILD_STAGGER_MS = 70;
  const MAX_STAGGERED_CHILDREN = 5;
  const SAFETY_TIMEOUT_MS = 2500;

  let targets = [];
  let observer = null;
  let settled = false;

  function prepare(section) {
    if (section.dataset.vReveal === 'ready') return;
    section.dataset.vReveal = 'ready';
    section.classList.add('v-reveal');

    const wrapper = section.querySelector('.section-content-wrapper, .layout-panel-flex');
    if (!wrapper) return;

    Array.from(wrapper.children)
      .slice(0, MAX_STAGGERED_CHILDREN)
      .forEach((child, i) => {
        if (i === 0) return; // first child rides the section's own transition
        child.classList.add('v-reveal-child');
        child.style.setProperty('--v-reveal-delay', `${i * CHILD_STAGGER_MS}ms`);
      });
  }

  function show(section) {
    if (!section || section.classList.contains('v-reveal--in')) return;
    section.classList.add('v-reveal--in');
    section.querySelectorAll('.v-reveal-child').forEach((c) => c.classList.add('v-reveal-child--in'));
    if (observer) observer.unobserve(section);
  }

  function revealAll() {
    settled = true;
    targets.forEach(show);
    document.querySelectorAll('.v-reveal-child').forEach((c) => c.classList.add('v-reveal-child--in'));
  }

  function inView(el) {
    const r = el.getBoundingClientRect();
    const h = window.innerHeight || document.documentElement.clientHeight;
    return r.top < h * 0.92 && r.bottom > 0;
  }

  // --- Trigger 2: position check, driven by any scroll or resize ---------------
  let ticking = false;
  function check() {
    if (ticking || settled) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      let remaining = 0;
      targets.forEach((s) => {
        if (s.classList.contains('v-reveal--in')) return;
        if (inView(s)) show(s);
        else remaining++;
      });
      if (remaining === 0) settled = true;
    });
  }

  function init() {
    const main = document.getElementById('MainContent');
    if (!main) return;

    // Index 0 is the hero on the homepage, the main product area elsewhere.
    targets = Array.from(main.querySelectorAll(':scope > .shopify-section')).slice(1);
    if (!targets.length) return;

    targets.forEach(prepare);

    // --- Trigger 1: IntersectionObserver ------------------------------------
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) show(e.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.06 }
      );
      targets.forEach((s) => observer.observe(s));
    }

    // --- Trigger 2 wiring ----------------------------------------------------
    // `true` puts these on the capture phase so scrolls inside any nested
    // container are seen, not just document-level scrolling.
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check, { passive: true });
    document.addEventListener('scroll', check, { passive: true, capture: true });
    document.addEventListener('wheel', check, { passive: true, capture: true });
    document.addEventListener('touchmove', check, { passive: true, capture: true });

    check();

    // --- Trigger 3: hard safety net -----------------------------------------
    setTimeout(revealAll, SAFETY_TIMEOUT_MS);
  }

  function boot() {
    init();
    // Re-measure once images have settled the layout.
    window.addEventListener('load', check, { once: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.closest('.shopify-section') || e.target;
    if (!section) return;
    section.dataset.vReveal = '';
    prepare(section);
    show(section);
  });
})();
